import React, { useMemo } from "react";

import { colors } from "@material-ui/core";

import { SquareArrowOutUpRight } from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";

import { LoadedDestinationInfo } from "@/types/global";
import { DestinationConfigRow } from "@/destinations/configs/shared/ConfigPageRow";

import { TermsOfServiceState } from "@/audiences";
import { Confirmation } from "@/components/icons/svg/figma/Confirmation";

type SimpleAudiencesTermsRowProps = {
  existingConfig: any;
  destination: LoadedDestinationInfo;
  setConfigModalOpen: (modalType: string) => void;
};

function tosStateFromSettings(
  settings: Record<string, any>,
): TermsOfServiceState {
  if (
    !settings ||
    !("termsOfServiceTestState" in settings) ||
    settings["termsOfServiceTestState"] === "tosUnknown"
  ) {
    return "tosUnknown";
  }

  if (settings["termsOfServiceTestState"] === "tosTestFailed") {
    return "tosTestFailed";
  }

  if (settings["termsOfServiceTestState"] === "tosTestSucceeded") {
    return "tosTestSucceeded";
  }

  return "tosUnknown";
}

export const MetaAudiencesTermsOfServiceRow = ({
  existingConfig,
  destination,
}: SimpleAudiencesTermsRowProps) => {
  const settings = existingConfig.settings || {};
  const idSet = "adAccountID" in settings;
  const tosState = tosStateFromSettings(settings);

  const textColor = tosState === "tosTestFailed" ? colors.red[500] : undefined;

  const labelText = useMemo(() => {
    switch (tosState) {
      case "tosTestFailed":
        return "Meta's terms of service for Custom Audiences must be accepted before you can use this destination.";
      case "tosTestSucceeded":
        return "Meta's terms of service for Custom Audiences were accepted.";
      default:
        return "Verify whether Meta's terms of service for Custom Audiences have been accepted before using this destination.";
    }
  }, [tosState]);

  const button: React.ReactNode = useMemo(() => {
    if (tosState === "tosTestSucceeded") {
      return <Confirmation />;
    }

    return (
      <a
        href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${settings.adAccountID}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <Button size="sm" trailingIcon={<SquareArrowOutUpRight />}>
          Review & Accept Terms
        </Button>
      </a>
    );
  }, [settings.adAccountID, tosState]);

  // The `MetaOAuth.tsx` component is used for both the Meta Audiences
  // destination, and for the Meta Insights destination. TOC is only
  // relevant for the audiences destination.
  if (destination.name !== "Meta Audiences") {
    return null;
  }

  return (
    <DestinationConfigRow
      name="Meta Custom Audiences Terms of Service"
      disabled={!idSet}
      textColor={textColor}
      labelText={labelText}
      labelStyles={{ maxWidth: 500, whiteSpace: "normal" }}
      onOpenConfigModal={() => {}}
      buttonComponent={button}
    />
  );
};
